import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendOutlinedIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { enotifyPhone } from "../services/whatsappService";

// Sending the interview invitation on WhatsApp, through the gateway rather than
// by hand.
//
// ─── WHY IT SHOWS THE MESSAGE ───────────────────────────────────────────────
//
// A WhatsApp message to a stranger cannot be unsent, and the number it goes to
// came off a spreadsheet import that nobody has read. Both of those are visible
// here — the resolved +91 number in the header, the words in the box — before
// anything is sent. That is the whole reason this is a dialog and not a button
// that fires.
//
// ─── WHY BULK IS NOT EDITABLE ───────────────────────────────────────────────
//
// Each candidate's message carries their own first name and their own signed
// interview link, so there is no single piece of text to edit: what you would
// be editing is one person's copy of it. The bulk view therefore shows the
// first candidate's message as a specimen, says plainly that it is one, and
// leaves the words to inviteMessage.js. Editing a single send is different —
// there is exactly one message and adding a line to it is a real thing to want.

const GREEN = "#25d366";
const GREEN_DARK = "#1ebe5d";

export default function WhatsAppInviteDialog({
  open,
  onClose,
  /** The candidates to message. One row is the editable single send. */
  rows = [],
  /** (row) => string — the pipeline's own inviteMessage, so the words match
   *  the emailed invitation and the clipboard. */
  buildMessage,
  /** Set while sending: { done, total }. */
  progress = null,
  /** (overrideMessage | null) => void. Null means "build each one per row". */
  onSend,
  /** Opens wa.me for one row, the manual route this replaces. Kept as the way
   *  through when the gateway is down, which it has been. */
  onOpenWhatsApp,
}) {
  const single = rows.length === 1 ? rows[0] : null;
  const [message, setMessage] = useState("");
  const sending = Boolean(progress);

  // Seeded when the dialog opens on a candidate, and not again.
  //
  // Keyed on the application id rather than on `single` or `buildMessage`: both
  // are rebuilt on every render of the pipeline, so depending on them would
  // re-seed the box whenever anything else on the page changed — including the
  // progress counter ticking during a send, which would visibly throw away an
  // edit while it was being sent.
  useEffect(() => {
    if (open && single && buildMessage) setMessage(buildMessage(single));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, single?.application_id]);

  // Who can actually be reached, worked out before anything is sent rather than
  // arriving as failures afterwards. The link is delivered by this message and
  // nothing else, so a row without a usable number is simply not being invited.
  const { reachable, unreachable } = useMemo(() => {
    const ok = [];
    const bad = [];
    for (const r of rows) (enotifyPhone(r.phone) ? ok : bad).push(r);
    return { reachable: ok, unreachable: bad };
  }, [rows]);

  const specimen = single
    ? message
    : rows.length && buildMessage
      ? buildMessage(rows[0])
      : "";

  const canSend = reachable.length > 0 && (!single || message.trim()) && !sending;

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ── Who it is going to ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: "linear-gradient(135deg, #075e54 0%, #128c7e 60%, #25d366 100%)",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,.15)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <WhatsAppIcon sx={{ color: "#fff", fontSize: 21 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15.5 }}>
              {single ? "Send the invitation" : `Send the invitation to ${reachable.length}`}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.85)", fontSize: 12.5 }} noWrap>
              {single ? (
                <>
                  {single.candidate_name}
                  {" · "}
                  <Box component="span" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                    +{enotifyPhone(single.phone) || "—"}
                  </Box>
                </>
              ) : (
                "Each one gets their own name and their own interview link"
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
          {/* No token warning and no acting-as warning here any more: the
              server resolves the gateway token from the store the request is
              scoped to, so the browser cannot know or get either one wrong. A
              store with no token configured comes back as a plain error on the
              send, naming what to fix. */}

          {/* Named, not counted. "3 have no number" makes somebody hunt for
              which three. */}
          {unreachable.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
              {unreachable.length === 1 && single ? (
                <>
                  <strong>{unreachable[0].candidate_name}</strong> has no usable phone
                  number{unreachable[0].phone ? ` — "${unreachable[0].phone}"` : ""}. The
                  gateway needs ten digits, or twelve starting 91.
                </>
              ) : (
                <>
                  No usable phone number for{" "}
                  <strong>{unreachable.map((r) => r.candidate_name).join(", ")}</strong>.
                  {reachable.length > 0
                    ? ` They will be skipped; the other ${reachable.length} will be sent.`
                    : ""}
                </>
              )}
            </Alert>
          )}

          {!single && reachable.length > 0 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 2 }}>
              {reachable.map((r) => (
                <Chip
                  key={r.application_id}
                  size="small"
                  label={`${r.candidate_name} · +${enotifyPhone(r.phone)}`}
                  sx={{ fontSize: 11.5, bgcolor: "#f4f4f5" }}
                />
              ))}
            </Stack>
          )}

          {/* ── The words ── */}
          <Box sx={{ border: "1px solid #e4e4e7", borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                bgcolor: "#ece5dd",
                borderBottom: "1px solid #e4e4e7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#52525b" }}>
                {single ? "Message — edit before sending" : "What each candidate gets"}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#71717a" }}>
                {specimen.length} chars
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={11}
              value={specimen}
              onChange={single ? (e) => setMessage(e.target.value) : undefined}
              variant="standard"
              disabled={sending}
              InputProps={{
                disableUnderline: true,
                readOnly: !single,
                sx: { p: 1.5, fontSize: 12.5, lineHeight: 1.7, fontFamily: "monospace" },
              }}
            />
          </Box>

          {!single && rows.length > 1 && (
            <Typography sx={{ fontSize: 11.5, color: "#a1a1aa", mt: 1, lineHeight: 1.55 }}>
              Shown for {rows[0]?.candidate_name}. The name, the interview link and the
              sign-off are filled in per candidate, so this one cannot be edited here — the
              wording lives in the invitation template.
            </Typography>
          )}

          {sending && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(progress.done / Math.max(1, progress.total)) * 100}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: "#e4e4e7",
                  "& .MuiLinearProgress-bar": { bgcolor: GREEN },
                }}
              />
              <Typography sx={{ fontSize: 12, color: "#71717a", mt: 0.75 }}>
                Sending {progress.done}/{progress.total}…
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2.5 }}>
            {/* The route this replaces, kept rather than removed: the gateway
                has been down before, and when it is, opening the chat by hand
                is the difference between a delayed invitation and none. */}
            {single && onOpenWhatsApp && (
              <Button
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                onClick={() => onOpenWhatsApp(single)}
                disabled={sending}
                sx={{ textTransform: "none", fontWeight: 600, color: "#71717a" }}
              >
                Open in WhatsApp instead
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={onClose}
              disabled={sending}
              sx={{ textTransform: "none", fontWeight: 650, color: "#71717a" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!canSend}
              onClick={() => onSend(single ? message : null)}
              startIcon={
                sending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SendOutlinedIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: GREEN,
                "&:hover": { bgcolor: GREEN_DARK },
                "&.Mui-disabled": { bgcolor: "#a5d6a7", color: "#fff" },
                minWidth: 120,
              }}
            >
              {sending
                ? "Sending…"
                : single
                  ? "Send"
                  : `Send ${reachable.length}`}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
